import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { Users, Plus, Trash2, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Employees() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/employees").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/employees", form);
      toast.success("Đã tạo tài khoản nhân viên");
      setForm({ name: "", phone: "", password: "" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/employees/${id}`);
      toast.success("Đã xóa nhân viên");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const inputCls = "w-full bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm px-4 py-2.5 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors placeholder:text-zinc-600";
  const labelCls = "block text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 mb-2";

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-white flex items-center gap-3">
            <Users className="w-9 h-9 text-primary" /> Nhân viên
          </h1>
          <p className="text-zinc-500 mt-2">Tạo và quản lý tài khoản nhân viên xem danh sách đen.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="add-employee-btn" className="flex items-center gap-2 bg-primary hover:bg-[#ff1a1a] text-white font-bold rounded-sm px-5 py-2.5 text-sm transition-colors">
              <Plus className="w-4 h-4" /> Thêm nhân viên
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0a0a] border-zinc-800">
            <DialogHeader><DialogTitle className="font-display text-white">Tạo tài khoản nhân viên</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div><label className={labelCls}>Tên nhân viên</label><input data-testid="emp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} required /></div>
              <div><label className={labelCls}>Số điện thoại (đăng nhập)</label><input data-testid="emp-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} required /></div>
              <div><label className={labelCls}>Mật khẩu</label><input data-testid="emp-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} required /></div>
              <button data-testid="emp-submit" disabled={loading} className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-[#ff1a1a] text-white font-bold rounded-sm px-6 py-2.5 transition-colors disabled:opacity-50">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Tạo tài khoản
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {rows.length === 0 ? (
        <p className="text-zinc-600 py-10 text-center">Chưa có nhân viên nào.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((e) => (
            <div key={e.id} data-testid={`employee-${e.id}`} className="bg-card border border-zinc-800 rounded-sm p-4 flex items-center justify-between">
              <div>
                <div className="font-semibold text-white">{e.name}</div>
                <div className="text-sm text-zinc-500">{e.phone}</div>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button data-testid={`delete-employee-${e.id}`} className="flex items-center gap-1 bg-red-950 text-red-500 border border-red-900 hover:bg-red-900 hover:text-white rounded-sm px-3 py-2 text-sm transition-colors">
                    <Trash2 className="w-4 h-4" /> Xóa
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#0a0a0a] border-zinc-800">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-white">Xóa nhân viên?</AlertDialogTitle>
                    <AlertDialogDescription className="text-zinc-400">Tài khoản {e.name} sẽ không thể đăng nhập được nữa.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-transparent border-zinc-700 text-zinc-300">Hủy</AlertDialogCancel>
                    <AlertDialogAction onClick={() => remove(e.id)} className="bg-primary hover:bg-[#ff1a1a] text-white">Xóa</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
